/**
 * Google Analytics (gtag.js) integration
 * - Environment-aware: only enabled when env.analytics.enabled is true
 * - No hardcoded IDs: reads measurement ID from env config
 * - Safe: no-ops when disabled or in non-browser environments
 */

import {env} from '@/shared/config/environment.ts';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function ensureGtagLoaded(measurementId: string): void {
  if (typeof window === 'undefined') return;

  // Avoid duplicate injection
  const existingScript = document.getElementById('ga-gtag-script');
  if (!existingScript) {
    const script = document.createElement('script');
    script.id = 'ga-gtag-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    document.head.appendChild(script);
  }

  // Initialize dataLayer and gtag
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
  if (!window.gtag) {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }

  // Basic init
  window.gtag('js', new Date());
}

/**
 * Initialize Google Analytics if enabled.
 * Sends initial pageview using current location.
 */
export function initAnalytics(): void {
  if (typeof window === 'undefined') return;

  const { analytics } = env;

  if (analytics.enabled && analytics.measurementId) {
    ensureGtagLoaded(analytics.measurementId);

    // Initial pageview
    const page_path = `${window.location.pathname}${window.location.search || ''}`;
    window.gtag?.('config', analytics.measurementId, { page_path });
  }
}

/**
 * Track a page view on route change.
 * Call this from a router effect.
 */
export function trackPageview(path?: string): void {
  if (typeof window === 'undefined') return;

  const { analytics } = env;

  if (analytics.enabled && analytics.measurementId) {
    const page_path = path ?? `${window.location.pathname}${window.location.search || ''}`;
    window.gtag?.('config', analytics.measurementId, { page_path });
  }
}
